/**
 * Lead-facing greeting prompt fragments
 * Owner: Prompt Engineer Agent. See docs/PROMPT_REGISTRY.md
 *
 * Must never omit: consulting posture, one research-grounded observation, no bottleneck survey
 */

import { formatResearchAckOpening } from '../agents/utils/working-hypothesis.js'
import { hasEmergencySiteSignal } from '../intelligence/grounded-research-claims.js'
import { splitCompanyUrlOrName } from '../../lib/company-search-label.js'
import {
  GENERIC_LIVE_IMPROV_MARKER,
  GREETING_HEDGE_MARKER,
  GREETING_HEDGE_MARKER_NB,
} from '../contracts.js'

type GreetingTranslator = (key: string, options?: Record<string, unknown>) => string

function isUsableGreetingCompany(value: string | null | undefined): value is string {
  const name = value?.replace(/\s+/g, ' ').trim()
  return Boolean(name && name !== 'your company' && !/^https?:\/\//i.test(name))
}

/** Unique hedge from greeting.default — replace this once research is on session. */
export function isHedgeWelcome(text: string | null | undefined): boolean {
  if (!text) return false
  const lower = text.toLowerCase()
  return (
    lower.includes(GREETING_HEDGE_MARKER.toLowerCase()) ||
    lower.includes(GREETING_HEDGE_MARKER_NB.toLowerCase())
  )
}

/** First consulting line — one natural observation, not a survey. */
export function isLabeledGuessWelcome(text: string | null | undefined): boolean {
  if (!text) return false
  return /i'd start here|jeg ville startet her|bottleneck guess|flaskehalsanslag/i.test(text)
}

/** Latest greeting-* bubble, including thin presence before research upgrades it. */
export function readLatestGreeting(
  transcript: Array<{ id?: string; role?: string; text?: string | null }>,
): string | null {
  for (let i = transcript.length - 1; i >= 0; i -= 1) {
    const item = transcript[i]
    if (
      item?.role === 'model' &&
      typeof item.id === 'string' &&
      item.id.startsWith('greeting-') &&
      item.text?.trim()
    ) {
      return item.text.replace(/\s+/g, ' ').trim()
    }
  }
  return null
}

/** Pack icebreaker — sourced issue + one automation + steer. Not a personal bottleneck. */
export function isIcebreakerWelcome(text: string | null | undefined): boolean {
  if (!text) return false
  return /one place ai helps|ett sted ai hjelper/i.test(text)
}

/** The homework sentence. A question-only opener is not this. */
export function isUngroundedResearchClaim(text: string | null | undefined): boolean {
  if (!text) return false
  return /i've already looked into|jeg har allerede sett|nok til å starte|enough to start/i.test(text)
}

/** Consultant frame, plus older acks so a saved transcript still mounts the card. */
export function isResearchAckWelcome(text: string | null | undefined): boolean {
  if (!text) return false
  const flat = text.replace(/\s+/g, ' ').trim()
  if (
    /^(?:hi|hei)(?:\s+[^.]{1,80})?\.\s+what are you looking to solve, improve, or explore\??$/i.test(
      flat,
    )
  ) {
    return true
  }
  if (
    /^(?:hi|hei)(?:\s+[^.]{1,80})?\.\s+hva vil du løse, forbedre eller utforske\??$/i.test(flat)
  ) {
    return true
  }
  return /i've already looked into|i've already started looking into|jeg har allerede sett|jeg har allerede begynt|i've looked into|i pulled public signals|nothing personal claimed|where do you want to steer|jeg har sett p[åa]|hvor vil du styre|du er hos .+ hvordan kan f\.b\/c hjelpe|nok til å starte|enough to start/i.test(
    flat,
  )
}

export function isFirstConsultingWelcome(text: string | null | undefined): boolean {
  return isLabeledGuessWelcome(text) || isIcebreakerWelcome(text) || isResearchAckWelcome(text)
}

/** Thin name+identity presence, or the old hedge — both upgrade when research lands. */
export function isUpgradeableWelcome(text: string | null | undefined): boolean {
  if (!text?.trim()) return false
  return isHedgeWelcome(text) || !isFirstConsultingWelcome(text)
}

const CANNED_BOTTLENECK_WELCOME =
  /turning the current growth priority into a repeatable workflow|turning incoming work into a reliable scheduling and follow-up flow|å gjøre vekstprioriteten om til en gjentakbar arbeidsflyt|å gjøre innkommende jobb om til pålitelig planlegging og oppfølging/i

/** Growth-workflow or scheduling canned line — the guess used when site text was missing. */
export function isCannedBottleneckWelcome(text: string | null | undefined): boolean {
  return Boolean(text && CANNED_BOTTLENECK_WELCOME.test(text))
}

/**
 * A labeled guess that used the canned line is replaced when later research
 * actually contains the 24h / akutt signal. Grounding on the old row does not
 * lock that weaker guess.
 */
export function shouldUpgradeCannedGuess(
  existing: string | null | undefined,
  next: string | null | undefined,
): boolean {
  if (!isCannedBottleneckWelcome(existing)) return false
  return hasEmergencySiteSignal(next) && !hasEmergencySiteSignal(existing)
}

/** Live mouth improv — not greeting.presence / default / researchedGuess. */
export function isGenericLiveImprovWelcome(text: string | null | undefined): boolean {
  if (!text) return false
  return text.toLowerCase().includes(GENERIC_LIVE_IMPROV_MARKER.toLowerCase())
}

/** Compare greeting / [SAY] echoes across en-dash, hyphen, and whitespace. */
export function normalizeGreetingCompareText(text: string | null | undefined): string {
  return (text ?? '')
    .replace(/[\u2013\u2014\u2212]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

/** Real visitor turn — not a [SAY] injection or hidden correction bind. */
export function transcriptHasVisitorTurn(
  transcript: Array<{ role?: string; text?: string | null }>,
): boolean {
  return transcript.some(item => {
    if (item.role !== 'user') return false
    const text = item.text?.trim() ?? ''
    if (!text) return false
    if (/^\s*\[SAY\]/i.test(text)) return false
    if (/HYPOTHESIS CORRECTION/i.test(text)) return false
    return true
  })
}

/**
 * First consulting [SAY] is once per session. After it has been spoken — or the
 * visitor has already taken a turn — never emit the greeting again.
 */
export function shouldSpeakFirstConsultingLine(input: {
  alreadySpoken?: string | null
  nextLine?: string | null
  visitorHasSpoken?: boolean
}): boolean {
  const next = input.nextLine?.replace(/\s+/g, ' ').trim() || ''
  if (!next) return false
  if (input.visitorHasSpoken) return false
  if (
    normalizeGreetingCompareText(input.alreadySpoken) === normalizeGreetingCompareText(next)
  ) {
    return false
  }
  return true
}

/** Latest greeting- bubble that already states the labeled guess. */
export function readLabeledGuessGreeting(
  transcript: Array<{ id?: string; role?: string; text?: string | null }>,
): string | null {
  for (let i = transcript.length - 1; i >= 0; i -= 1) {
    const item = transcript[i]
    if (
      item?.role === 'model' &&
      typeof item.id === 'string' &&
      item.id.startsWith('greeting-') &&
      isFirstConsultingWelcome(item.text)
    ) {
      return item.text!.replace(/\s+/g, ' ').trim()
    }
  }
  return null
}

/** Stated company wins; then research; email-domain last. Never a URL or placeholder. */
export function resolveGreetingCompanyName(input: {
  statedCompany?: string | null | undefined
  researchCompany?: string | null | undefined
  verificationCompany?: string | null | undefined
  emailDomainCompany?: string | null | undefined
}): string {
  const candidates = [
    input.statedCompany,
    input.researchCompany,
    input.verificationCompany,
    input.emailDomainCompany,
  ]
  for (const candidate of candidates) {
    if (isUsableGreetingCompany(candidate)) return candidate.replace(/\s+/g, ' ').trim()
  }
  return 'your company'
}

/**
 * Plate Company is the opener name when the visitor typed one.
 * A URL plate is not a name. A capitalized email-domain label is not a name.
 * An empty plate stays unnamed. The string is not re-cased or split.
 */
export function openerCompanyName(input: {
  plate?: string | null | undefined
  statedCompany?: string | null | undefined
  researchCompany?: string | null | undefined
  verificationCompany?: string | null | undefined
  emailDomainCompany?: string | null | undefined
}): string {
  const plateName = splitCompanyUrlOrName(input.plate).statedCompany
  return resolveGreetingCompanyName({
    statedCompany: plateName ?? input.statedCompany,
    researchCompany: input.researchCompany,
    verificationCompany: input.verificationCompany,
  })
}

const defaultGreetingTranslator: GreetingTranslator = (key, options) => {
  const name = String(options?.name ?? '')
  const companyName = String(options?.companyName ?? '')
  const location = String(options?.location ?? '')
  const detail = String(options?.detail ?? '')
  const count = Number(options?.count ?? 0)

  switch (key) {
    case 'greeting.default':
    case 'greeting.presence':
      return `Hi ${name}. I'm F.B/c — Farzad's consulting AI.`
    case 'greeting.educational':
      return `Hi ${name}. You're in explore mode — I can explain how an AI consultant runs intake, research, discovery, and recommendations without a sales pitch. Which part do you want to understand first?`
    case 'greeting.trustedWithLocation':
      return `Hi ${name}. You're building ${companyName} in ${location}.`
    case 'greeting.trusted':
      return `Hi ${name}. I've got ${companyName} in front of me.`
    case 'greeting.publicContext':
      return `Hi ${name}. ${companyName} is the company I'm starting from.`
    case 'greeting.researched':
      return formatResearchAckOpening({ name, companyName, sourceCount: 0 })
    case 'greeting.researchedGuess':
      return formatResearchAckOpening({
        name,
        companyName,
        industry: String(options?.industry ?? ''),
        sourceCount: 0,
      })
    case 'greeting.intakeCompany':
      return `Hi ${name}. ${companyName} is what I have from intake.`
    case 'greeting.intakeOnly':
      return `Hi ${name}. I'm F.B/c — Farzad's consulting AI.`
    case 'concierge.whyNowBackedProfile':
      return `This profile is backed by ${count} public source${count === 1 ? '' : 's'} — useful for grounding our conversation in your real context.`
    case 'concierge.whyNowPrefix':
      return `Why this matters now: ${detail}`
    default:
      return key
  }
}

export function getLeadResearchGreeting(
  name: string,
  _companyName: string,
  t: GreetingTranslator = defaultGreetingTranslator
): string {
  return t('greeting.default', { name })
}

export function getLeadResearchGreetingEducational(
  name: string,
  t: GreetingTranslator = defaultGreetingTranslator
): string {
  return t('greeting.educational', { name })
}

export function isNoSaleEducationLead(email: string, name?: string | null): boolean {
  const combined = `${email} ${name || ''}`.toLowerCase()
  return (
    /\.edu(?:\b|$)/i.test(email) ||
    /\bintern\b|\bstudent\b|\bschool\b|\bassignment\b|\bhomework\b|\bnot buying\b|\bjust (?:testing|learning|browsing)\b|\bhow ai consultants work\b/.test(
      combined
    )
  )
}

export function getLeadResearchGreetingRich(
  name: string,
  companyName: string,
  context?: {
    role?: string | null
    industry?: string | null
    location?: string | null
    hasStrategicInsights?: boolean
    sourceCount?: number
    trustedIdentity?: boolean
    hasBriefingCard?: boolean
    strategicHook?: string | null
    locale?: string | null
    siteSignal?: string | null
    /** Ignored for the spoken line. Pack facts stay in the model seed. */
    icebreaker?: string | null
  },
  _t: GreetingTranslator = defaultGreetingTranslator
): string {
  return formatResearchAckOpening({
    name,
    companyName,
    industry: context?.industry,
    locale: context?.locale,
    siteSignal: context?.siteSignal,
    ...(context && typeof context.sourceCount === 'number'
      ? { sourceCount: context.sourceCount }
      : {}),
  })
}
